import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import RecipientEnabledField from '@/components/RecipientEnabledField';
import { useWorkspace } from '@/context/WorkspaceContext';
import type { Recipient } from '@/types';
import {
  AppDropdownSelect,
  AppFieldGrid,
  AppFieldGridSpan,
  AppFormField,
  AppInput,
} from '@/components/ui';
import { formatApiError } from '@/utils/formatApiError';
import {
  getRecipientVisibilityOptions,
  resolveRecipientVisibility,
  toApiRecipientVisibility,
} from '@/utils/recipientVisibility';
import {
  DELIVERY_PROVIDER_OPTIONS,
  type TelegramDeliveryProvider,
} from '@/utils/knownChatRecipient';
import {
  isPlainUsername,
  meRecipientWarning,
  type TelegramSenderMode,
} from '@/utils/telegramErrors';

type Props = {
  recipient: Recipient;
  botConfigured: boolean;
  sessionExists: boolean;
  senderMode: TelegramSenderMode;
  onClose: () => void;
  onSaved: (message: string) => void;
  onError?: (message: string) => void;
};

function normalizeTelegramUsername(value: string): string {
  return value.trim().replace(/^@+/, '');
}

const CHANNEL_OPTIONS = [{ value: 'telegram', label: 'Telegram' }];

export default function EditRecipientModal({
  recipient,
  botConfigured,
  sessionExists,
  senderMode,
  onClose,
  onSaved,
  onError,
}: Props) {
  const { isPersonalMode } = useWorkspace();
  const visibilityOptions = useMemo(
    () => getRecipientVisibilityOptions(!isPersonalMode),
    [isPersonalMode],
  );

  const [channel, setChannel] = useState(recipient.channel || 'telegram');
  const [chatId, setChatId] = useState(recipient.chat_id);
  const [telegramUsername, setTelegramUsername] = useState(recipient.telegram_username || '');
  const [displayName, setDisplayName] = useState(recipient.display_name || '');
  const [isEnabled, setIsEnabled] = useState(recipient.is_enabled);
  const [visibility, setVisibility] = useState<'private' | 'workspace'>(() =>
    resolveRecipientVisibility(recipient.visibility, isPersonalMode),
  );
  const [deliveryProvider, setDeliveryProvider] = useState<TelegramDeliveryProvider>(
    recipient.delivery_provider || 'auto',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [chatIdError, setChatIdError] = useState('');

  useEffect(() => {
    setChannel(recipient.channel || 'telegram');
    setChatId(recipient.chat_id);
    setTelegramUsername(recipient.telegram_username || '');
    setDisplayName(recipient.display_name || '');
    setIsEnabled(recipient.is_enabled);
    setVisibility(resolveRecipientVisibility(recipient.visibility, isPersonalMode));
    setDeliveryProvider(recipient.delivery_provider || 'auto');
    setChatIdError('');
    setError('');
  }, [recipient, isPersonalMode]);

  useEffect(() => {
    if (isPersonalMode && visibility === 'workspace') {
      setVisibility('private');
    }
  }, [isPersonalMode, visibility]);

  const trimmedChatId = chatId.trim();
  const chatIdInvalid = !trimmedChatId;
  const chatIdValidationMessage = chatIdError || (chatIdInvalid ? 'Chat ID is required' : '');
  const canSave = !chatIdInvalid && !saving;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const normalizedChatId = trimmedChatId.replace(/^@+/, '');
    if (!normalizedChatId) {
      const message = 'Chat ID is required';
      setChatIdError(message);
      setError(message);
      onError?.(message);
      return;
    }
    setSaving(true);
    setError('');
    setChatIdError('');
    try {
      const normalizedUsername = telegramUsername.trim()
        ? normalizeTelegramUsername(telegramUsername)
        : '';
      await api.recipients.update(recipient.id, {
        channel,
        chat_id: normalizedChatId,
        display_name: displayName.trim() || normalizedChatId,
        is_enabled: isEnabled,
        telegram_username: normalizedUsername,
        visibility: toApiRecipientVisibility(visibility),
        delivery_provider: deliveryProvider,
      });
      onSaved('Recipient updated');
      onClose();
    } catch (err) {
      const message = formatApiError(err, 'Update failed');
      setError(message);
      onError?.(message);
    } finally {
      setSaving(false);
    }
  }

  const showMeWarning = channel === 'telegram' && trimmedChatId.toLowerCase() === 'me';
  const telethonAvailable = sessionExists || senderMode === 'telethon';
  const showUsernameWarning =
    channel === 'telegram' &&
    botConfigured &&
    !telethonAvailable &&
    senderMode === 'bot' &&
    isPlainUsername(trimmedChatId);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal card card-elevated recipient-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="edit-recipient-title"
      >
        <h2 id="edit-recipient-title">Edit recipient</h2>
        <p className="muted modal-hint">
          Notification delivery target — not a Team management user.
        </p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <form className="recipient-form" onSubmit={save} noValidate>
          <AppFieldGrid columns={2}>
            <AppFormField label="Channel">
              <AppDropdownSelect
                value={channel}
                options={CHANNEL_OPTIONS}
                onChange={setChannel}
                disabled
                aria-label="Channel"
              />
            </AppFormField>
            <AppFormField label="Display name">
              <AppInput
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="optional"
              />
            </AppFormField>
            <AppFieldGridSpan span="full">
              <AppFormField
                label="Destination / Chat ID"
                required
                error={chatIdValidationMessage}
                helperText="Use username without @, numeric chat ID, or resolve with Telethon."
              >
                <AppInput
                  value={chatId}
                  onChange={(e) => {
                    setChatId(e.target.value);
                    if (chatIdError) setChatIdError('');
                  }}
                  placeholder="numeric id, me, or username"
                  hasError={Boolean(chatIdValidationMessage)}
                />
              </AppFormField>
            </AppFieldGridSpan>
            {showMeWarning ? (
              <AppFieldGridSpan span="full">
                <p className="recipient-form__note muted">{meRecipientWarning(senderMode)}</p>
              </AppFieldGridSpan>
            ) : null}
            {showUsernameWarning ? (
              <AppFieldGridSpan span="full">
                <p className="recipient-form__note recipient-form__note--warn muted">
                  Username recipients need /start with the bot, then Resolve Telegram ID.
                </p>
              </AppFieldGridSpan>
            ) : null}
            <AppFormField label="Telegram username">
              <AppInput
                value={telegramUsername}
                onChange={(e) => setTelegramUsername(e.target.value)}
                placeholder="username (optional)"
              />
            </AppFormField>
            <AppFormField label="Telegram delivery method">
              <AppDropdownSelect
                value={deliveryProvider}
                options={DELIVERY_PROVIDER_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                onChange={(next) => setDeliveryProvider(next as TelegramDeliveryProvider)}
                aria-label="Telegram delivery method"
              />
            </AppFormField>
            <AppFormField label="Visibility">
              <AppDropdownSelect
                value={visibility}
                options={visibilityOptions}
                onChange={(next) => setVisibility(next as 'private' | 'workspace')}
                aria-label="Visibility"
              />
            </AppFormField>
            <AppFieldGridSpan span="full">
              <RecipientEnabledField
                id="recipient-edit-enabled"
                checked={isEnabled}
                onChange={setIsEnabled}
              />
            </AppFieldGridSpan>
          </AppFieldGrid>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!canSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { normalizeTelegramUsername };
